/**
 * The inputs the profile entry card is given.
 *
 * `resolveEntryState` in the card is the state machine and it is correct; it was
 * simply never fed. Both call sites passed `hasReport={false}` as a literal and
 * nothing else, which froze the founder card at `not_generated` and the investor
 * card at `unavailable` — an action of null, so a published report was
 * unreachable from the profile.
 *
 * Derived here rather than inline at each call site so the two cannot drift, and
 * so the result can be spread as one object: a prop that arrives from a variable
 * cannot quietly become a literal again.
 *
 * THE PERSONA RULE IS ENFORCED HERE TOO. Readiness and `sourcesChanged` describe
 * founder-private material — which fields are missing, which sources disagree —
 * about a company that is not an investor's. The draft they come from is already
 * a founder-only fetch, but this refuses to pass them to a non-member regardless
 * of what a caller hands in.
 */

import type { ServerReadiness } from './types';

/** Non-terminal statuses: work is still in flight. */
const IN_FLIGHT = new Set(['queued', 'researching', 'synthesizing', 'validating']);

export interface EntryStateInput {
  /** One row per run. Null while loading. */
  runs: { analysis_type?: string; status?: string; input_fingerprint?: string }[] | null | undefined;
  /**
   * Today's fingerprint per analysis type, from the server. Undefined while
   * loading — which must read as "not known yet", never as stale.
   */
  fingerprints?: Record<string, string> | null;
  /** Founder-only. Null for an investor or before it loads. */
  draft: {
    sourcesChanged?: boolean;
    readiness?: Record<string, ServerReadiness | undefined>;
  } | null | undefined;
  canEdit: boolean;
}

export interface EntryState {
  hasReport: boolean;
  isProcessing: boolean;
  isStale: boolean;
  readiness: ServerReadiness | null;
  sourcesChanged: boolean;
}

export function deriveEntryState({ runs, draft, canEdit, fingerprints }: EntryStateInput): EntryState {
  const rows = Array.isArray(runs) ? runs : [];

  // Only `ready` is a report. `insufficient_evidence` is a published RUN but not
  // a document to read, and offering "View" for it would open an empty screen.
  const hasReport = rows.some((row) => row.status === 'ready');
  const isProcessing = rows.some((row) => IN_FLIGHT.has(row.status ?? ''));

  // Only a READY report can be out of date: an in-flight run is working against
  // current inputs by definition, and a failed one has nothing to refresh. An
  // absent fingerprint means NOT KNOWN YET — guessing stale would put a refresh
  // prompt on a perfectly current report.
  const isStale = rows.some((row) => {
    if (row.status !== 'ready') return false;
    const current = fingerprints?.[row.analysis_type ?? ''];
    return Boolean(current && row.input_fingerprint && current !== row.input_fingerprint);
  });

  if (!canEdit) {
    return { hasReport, isProcessing, isStale: false, readiness: null, sourcesChanged: false };
  }

  // The FIRST blocking analysis. The card offers one action, so it needs one
  // reason; the focused form on the other side asks for everything blocking.
  const blocking = Object.values(draft?.readiness ?? {}).find(
    (entry): entry is ServerReadiness => Boolean(entry) && entry!.status !== 'ready',
  ) ?? null;

  return {
    hasReport,
    isProcessing,
    isStale,
    readiness: blocking,
    sourcesChanged: draft?.sourcesChanged === true,
  };
}
